import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import {
  ScheduleMemberSource,
  ScheduleStatus,
  type ProductScheduleListItem,
  type ScheduleMember,
  type StaffProfileStoresItem,
} from '@nucleus-commerce/api-client/admin';

/** Fields GET /org/schedules sorts on. */
export const SCHEDULE_SORTS = ['name', 'nextRunAt', 'createdAt'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/** The list filters as the URL and the API spell them (status is a comma list, one store). */
export const SCHEDULE_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(ScheduleStatus) },
  { key: 'storeId', type: 'async' },
] as const;

const matches = (needle: string, label: string) =>
  !needle.trim() || label.toLowerCase().includes(needle.trim().toLowerCase());

export function scheduleFilters({
  t,
  stores,
}: {
  /** The `schedules` namespace. */
  t: TFunction;
  stores: ReadonlyArray<Pick<StaffProfileStoresItem, 'id' | 'name'>>;
}): Array<FilterDef<ProductScheduleListItem>> {
  const storeOptions = stores.map((s): FilterOption => ({ value: s.id, label: s.name }));
  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: Object.values(ScheduleStatus).map((value) => ({
        value,
        label: t(`status.${value}`),
      })),
    },
    {
      key: 'storeId',
      label: t('filters.store'),
      type: 'async',
      pinned: true,
      multiple: false,
      aliases: ['store'],
      debounceMs: 0,
      loadOptions: (query) => Promise.resolve(storeOptions.filter((o) => matches(query, o.label))),
      resolveOptions: (values) =>
        Promise.resolve(storeOptions.filter((o) => values.includes(o.value))),
    },
  ];
}

/** Pool filters: pinned, excluded, source (manual or rule); `q` searches title and handle. */
export const MEMBER_FILTER_FIELDS = [
  { key: 'pinned', type: 'boolean' },
  { key: 'excluded', type: 'boolean' },
  { key: 'source', type: 'enum', options: valuesOf(ScheduleMemberSource) },
] as const;

export function memberFilters(t: TFunction): Array<FilterDef<ScheduleMember>> {
  return [
    {
      key: 'pinned',
      label: t('pool.filters.pinned'),
      type: 'boolean',
      pinned: true,
      trueLabel: t('pool.filters.yes'),
      falseLabel: t('pool.filters.no'),
    },
    {
      key: 'excluded',
      label: t('pool.filters.excluded'),
      type: 'boolean',
      pinned: true,
      trueLabel: t('pool.filters.yes'),
      falseLabel: t('pool.filters.no'),
    },
    {
      key: 'source',
      label: t('pool.filters.source'),
      type: 'enum',
      pinned: true,
      options: Object.values(ScheduleMemberSource).map((value) => ({
        value,
        label: t(`source.${value}`),
      })),
    },
  ];
}
